/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import React, { useEffect, useState, ChangeEvent, KeyboardEvent } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from 'redux/store';

import { StyledChatInputField, StyledInputField } from './ChatInput.styled';
import PaperPlane from 'assets/icons/PaperPlane';
import { Sender } from '../../../../ChatNew/ChatBodyNew/ChatBodyNew';
import api from 'api';
import { Tooltip } from '@mui/material';

interface Props {
    setMessages: React.Dispatch<React.SetStateAction<any[]>>;
    room: string;
    chatUserIds: number[];
}

export default function ChatInput(props: Props) {
    const [inputValue, setInputValue] = useState<string>('');
    const [hasAccess, setHasAccess] = useState<boolean>(false);
    const { id: userId } = useSelector((state: RootState) => state.user.entity);
    const { setMessages, room } = props;
    const { t } = useTranslation();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);

    const generateTempId = () => `temp-${Date.now()}`;

    useEffect(() => {
        let isCancelled = false;
        (async () => {
            try {
                const res = await api.chatNew.has_access(parseInt(room));
                if (!isCancelled) {
                    setHasAccess(Boolean(res.data?.has_access));
                }
            } catch {
                if (!isCancelled) {
                    setHasAccess(false);
                }
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [room]);

    useEffect(() => {
        if (!room) {
            return;
        }

        let lastPolledAt = new Date().toISOString();
        let requestInFlight = false;

        const interval = setInterval(async () => {
            if (requestInFlight) {
                return;
            }

            requestInFlight = true;
            const pollStartedAt = new Date().toISOString();

            try {
                const res = await api.chatNew.poll_messages(parseInt(room), lastPolledAt);
                const newMessages: any[] = res.data;
                if (newMessages.length > 0) {
                    setMessages((prevMessages) => {
                        const existingIds = new Set(
                            prevMessages
                                .map((message) => message.id)
                                .filter((id) => id !== undefined && id !== null)
                        );
                        const otherUserMessages = newMessages.filter((msg) => {
                            if (msg.user_id === userId || existingIds.has(msg.id)) {
                                return false;
                            }

                            existingIds.add(msg.id);
                            return true;
                        });

                        if (otherUserMessages.length === 0) {
                            return prevMessages;
                        }

                        return [
                            ...prevMessages,
                            ...otherUserMessages.map((msg) => ({
                                id: msg.id,
                                sender: msg.user_id === null ? Sender.ADMIN : Sender.OTHER,
                                message: msg.text,
                                createdAt: msg.created_at,
                                user: msg.user,
                            })),
                        ];
                    });
                }
                lastPolledAt = pollStartedAt;
            } catch (error) {
                console.error('Polling error:', error);
            } finally {
                requestInFlight = false;
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [userId, room, setMessages]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleSendMessage = async () => {
        if (inputValue.trim() !== '') {
            const message = inputValue.trim();
            const tempId = generateTempId();
            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    sender: Sender.ME,
                    message: message,
                    createdAt: new Date().toISOString(),
                    tempId: tempId,
                },
            ]);
            setInputValue('');
            try {
                await api.chatNew.send_message(parseInt(room), message);
            } catch (error) {
                console.error('Send message error:', error);
            }
        }
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSendMessage();
        }
    };

    const isInputDisabled = !hasAccess;

    return (
        <StyledChatInputField>
            <Tooltip
                title={isInputDisabled ? t('youAreNotAMemberOfThisChat', { lng: language }) : ''}
                placement={'top'}
            >
                <StyledInputField
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Write a message..."
                    disabled={isInputDisabled}
                />
            </Tooltip>
            <PaperPlane onClick={handleSendMessage} />
        </StyledChatInputField>
    );
}
